import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { VehicleStateLocal } from '../models/vehicle-state-local.model';
import { UnitTransmissionStatusService } from 'src/app/units/services/unit-transmission-status.service';
import { first, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class StateVehicleService {
  private state = new BehaviorSubject<Map<string, VehicleStateLocal>>(
    new Map()
  );
  private isLoading = new BehaviorSubject<boolean>(true);
  private initialized = false;

  state$ = this.state.asObservable();
  isLoading$ = this.isLoading.asObservable();

  constructor(unitTransmissionStatusService: UnitTransmissionStatusService) {
    unitTransmissionStatusService.getUnitsTransmissionStatus
      .pipe(
        tap((units) => {
          // Solo inicializamos si hay datos válidos y no se ha inicializado antes
          if (!this.initialized && units && units.length > 0) {
            const initialState = new Map<string, VehicleStateLocal>();

            units.forEach((unit) => {
              initialState.set(unit.GNSSIMEI, {
                isExpanded: false,
                details: {
                  firstSectionOpen: false,
                  secondSectionOpen: true,
                },
                viewOptions: {
                  showDriver: false,
                  showName: true,
                  follow: false,
                  visible: true,
                },
              });
            });
            this.state.next(initialState);
            this.isLoading.next(false);
            this.initialized = true;
          }
        })
      )
      .subscribe();
  }

  get getStatesVehicle() {
    return this.state$.pipe(map((state) => Array.from(state.values())));
  }

  getStateByImei(imei: string): VehicleStateLocal | undefined {
    return this.state.value.get(imei);
  }

  toggleExpanded(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        isExpanded: !currentState.isExpanded,
      });
    } else {
      currentMap.set(imei, {
        isExpanded: true,
        details: {
          firstSectionOpen: false,
          secondSectionOpen: true,
        },
        viewOptions: {
          showDriver: false,
          showName: true,
          follow: false,
          visible: true,
        },
      });
    }

    this.state.next(currentMap);
  }

  toggleFirstSection(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        details: {
          ...currentState.details,
          firstSectionOpen: !currentState.details.firstSectionOpen,
        },
      });
      this.state.next(currentMap);
    }
  }

  toggleSecondSection(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        details: {
          ...currentState.details,
          secondSectionOpen: !currentState.details.secondSectionOpen,
        },
      });
      this.state.next(currentMap);
    }
  }

  toggleShowDriver(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        viewOptions: {
          ...currentState.viewOptions,
          showDriver: !currentState.viewOptions.showDriver,
        },
      });
      this.state.next(currentMap);
    }
  }

  toggleShowName(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        viewOptions: {
          ...currentState.viewOptions,
          showName: !currentState.viewOptions.showName,
        },
      });
      this.state.next(currentMap);
    }
  }

  toggleFollow(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        viewOptions: {
          ...currentState.viewOptions,
          follow: !currentState.viewOptions.follow,
        },
      });
      this.state.next(currentMap);
    }
  }

  toggleVisible(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        viewOptions: {
          ...currentState.viewOptions,
          visible: !currentState.viewOptions.visible,
        },
      });
      this.state.next(currentMap);
    }
  }

  setTrueVisible(imei: string): void {
    const currentMap = new Map(this.state.value);
    const currentState = currentMap.get(imei);
    if (currentState) {
      currentMap.set(imei, {
        ...currentState,
        viewOptions: {
          ...currentState.viewOptions,
          visible: true,
        },
      });
      this.state.next(currentMap);
    }
  }

  setVisibleGlobal(value: boolean): void {
    const currentMap = new Map(this.state.value);
    // Iterar sobre todos los IMEIs en el Map
    currentMap.forEach((state, imei) => {
      currentMap.set(imei, {
        ...state,
        viewOptions: {
          ...state.viewOptions,
          visible: value,
        },
      });
    });

    this.state.next(currentMap);
  }

  setVisibleGroup(imeis: string[], value: boolean): void {
    const currentMap = new Map(this.state.value);

    // Iterar sobre los IMEIs específicos
    imeis.forEach((imei) => {
      const currentState = currentMap.get(imei);
      if (currentState) {
        currentMap.set(imei, {
          ...currentState,
          viewOptions: {
            ...currentState.viewOptions,
            visible: value,
          },
        });
      }
    });

    this.state.next(currentMap);
  }
}
